import React, { forwardRef, useImperativeHandle, useMemo, useState } from 'react';
import { LuChevronDown, LuChevronUp, LuCircleAlert, LuCircleCheck, LuLoader } from 'react-icons/lu';
import { apiErrorMessage, multipartFormConfig } from '../../utils/multipartUpload';

const QUESTION_PRESETS = [
  { id: 'AUTO', label: 'Auto (recommended)' },
  { id: '10', label: 'Up to 10' },
  { id: '25', label: 'Up to 25' },
  { id: '50', label: 'Up to 50' },
  { id: '100', label: 'Up to 100' },
  { id: 'CUSTOM', label: 'Custom max' },
];

const DIFFICULTY = [
  { id: 'MIXED', label: 'Mixed levels' },
  { id: 'BEGINNER', label: 'Beginner' },
  { id: 'INTERMEDIATE', label: 'Intermediate' },
  { id: 'ADVANCED', label: 'Advanced' },
];

const FOCUS = [
  { id: 'MIXED', label: 'Mixed' },
  { id: 'CONCEPTUAL', label: 'Conceptual' },
  { id: 'INTERVIEW', label: 'Interview-style' },
  { id: 'PRACTICAL', label: 'Practical' },
];

function formatBytes(n) {
  if (!n) return '—';
  if (n < 1024 * 1024) return `${Math.round(n / 1024)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

function LearnPdfImportPanel({
  notebookId,
  apiClient,
  onJobStarted,
  onError,
  busy,
  setBusy,
  dragOver,
}, ref) {
  const [pdfFile, setPdfFile] = useState(null);
  const [analyze, setAnalyze] = useState(null);
  const [analyzing, setAnalyzing] = useState(false);
  const [pageFrom, setPageFrom] = useState(1);
  const [pageTo, setPageTo] = useState(1);
  const [questionMode, setQuestionMode] = useState('AUTO');
  const [customQuestions, setCustomQuestions] = useState('');
  const [difficulty, setDifficulty] = useState('MIXED');
  const [focus, setFocus] = useState('MIXED');
  const [agreed, setAgreed] = useState(false);
  const [howOpen, setHowOpen] = useState(false);

  const pageCount = analyze?.pageCount || 1;
  const rangeOk = pageFrom >= 1 && pageTo >= pageFrom && pageTo <= pageCount;

  const liveEstimates = useMemo(() => {
    if (!analyze?.canProcess || !rangeOk) return null;
    const span = pageTo - pageFrom + 1;
    const ratio = analyze.pageCount > 0 ? span / analyze.pageCount : 1;
    const chunks = Math.max(1, Math.round((analyze.estimatedChunks || 1) * ratio));
    const questions = Math.max(
      1,
      Math.min(
        analyze.planMaxQuestionsPerImport || 100,
        Math.round((analyze.estimatedQuestionsAuto || 8) * ratio),
      ),
    );
    return { chunks, questions, span };
  }, [analyze, pageFrom, pageTo, rangeOk]);

  const resetForNewFile = () => {
    setAnalyze(null);
    setAgreed(false);
    setPageFrom(1);
    setPageTo(1);
  };

  const runAnalyze = async (file) => {
    if (!file) return;
    setPdfFile(file);
    resetForNewFile();
    setAnalyzing(true);
    onError?.('');
    const form = new FormData();
    form.append('file', file, file.name || 'document.pdf');
    try {
      const res = await apiClient.post(
        `/api/v1/learn/notebooks/${notebookId}/documents/analyze`,
        form,
        multipartFormConfig,
      );
      const data = res.data;
      setAnalyze(data);
      setPageFrom(data.defaultPageFrom || 1);
      setPageTo(data.defaultPageTo || data.pageCount || 1);
    } catch (e) {
      onError?.(apiErrorMessage(e, 'Could not analyze this PDF'));
      setPdfFile(null);
    } finally {
      setAnalyzing(false);
    }
  };

  const startImport = async () => {
    if (!pdfFile || !analyze?.canProcess || !agreed || !rangeOk) return;
    let questionCount = questionMode;
    if (questionMode === 'CUSTOM') {
      const n = parseInt(customQuestions, 10);
      if (!n || n < 1) {
        onError?.('Enter a valid custom question limit.');
        return;
      }
      questionCount = String(Math.min(n, analyze.planMaxQuestionsPerImport || n));
    }
    setBusy(true);
    onError?.('');
    const form = new FormData();
    form.append('file', pdfFile, pdfFile.name || 'document.pdf');
    form.append('questionCount', questionCount);
    form.append('difficulty', difficulty);
    form.append('focus', focus);
    form.append('pageFrom', String(pageFrom));
    form.append('pageTo', String(pageTo));
    try {
      const res = await apiClient.post(
        `/api/v1/learn/notebooks/${notebookId}/documents`,
        form,
        multipartFormConfig,
      );
      onJobStarted?.(res.data);
      setPdfFile(null);
      setAnalyze(null);
      setAgreed(false);
    } catch (e) {
      onError?.(apiErrorMessage(e, 'PDF upload failed'));
    } finally {
      setBusy(false);
    }
  };

  const pickFile = (file) => {
    if (!file) return;
    if (!file.name?.toLowerCase().endsWith('.pdf') && file.type !== 'application/pdf') {
      onError?.('Please choose a PDF file.');
      return;
    }
    runAnalyze(file);
  };

  useImperativeHandle(ref, () => ({ pickFile }), [onError]);

  return (
    <>
      <div className="learn-import-drop-hint">
        {dragOver ? 'Drop PDF here' : 'Drag & drop a PDF, or choose a file — we will analyze it before processing'}
      </div>
      <button
        type="button"
        className="home-btn home-btn-primary learn-btn--compact"
        disabled={busy || analyzing}
        onClick={() => document.getElementById('learn-pdf-file-input')?.click()}
      >
        {analyzing ? 'Analyzing PDF…' : busy ? 'Starting…' : 'Choose PDF file'}
      </button>
      <input
        id="learn-pdf-file-input"
        type="file"
        accept="application/pdf,.pdf"
        hidden
        onChange={(e) => pickFile(e.target.files?.[0])}
      />

      {analyzing && (
        <p className="learn-pdf-analyze-wait" role="status">
          <LuLoader className="learn-import-spinner" aria-hidden />
          Scanning pages, embedded text, and OCR readiness…
        </p>
      )}

      {analyze && !analyzing && (
        <div className="learn-pdf-preflight">
          <div className={`learn-pdf-preflight-banner${analyze.canProcess ? ' is-ok' : ' is-error'}`}>
            {analyze.canProcess ? <LuCircleCheck aria-hidden /> : <LuCircleAlert aria-hidden />}
            <div>
              <p className="learn-pdf-preflight-title">{analyze.filename}</p>
              <p className="learn-muted learn-pdf-preflight-msg">{analyze.message}</p>
            </div>
          </div>

          <dl className="learn-pdf-stats">
            <div><dt>Pages</dt><dd>{analyze.pageCount}</dd></div>
            <div><dt>With text</dt><dd>{analyze.pagesWithExtractableText}</dd></div>
            <div><dt>Size</dt><dd>{formatBytes(analyze.fileSizeBytes)}</dd></div>
            <div><dt>Plan</dt><dd>{analyze.plan}</dd></div>
          </dl>

          {analyze.canProcess && (
            <>
              <fieldset className="learn-pdf-fieldset">
                <legend>Page range</legend>
                <p className="learn-muted learn-pdf-field-hint">
                  Process all pages or a slice (e.g. chapter 1–20). Scanned or handwritten pages use OCR when embedded text is missing.
                </p>
                <div className="learn-pdf-range-row">
                  <label>
                    From
                    <input
                      type="number"
                      className="learn-input learn-pdf-num"
                      min={1}
                      max={pageCount}
                      value={pageFrom}
                      onChange={(e) => setPageFrom(Number(e.target.value))}
                      disabled={busy}
                    />
                  </label>
                  <label>
                    To
                    <input
                      type="number"
                      className="learn-input learn-pdf-num"
                      min={1}
                      max={pageCount}
                      value={pageTo}
                      onChange={(e) => setPageTo(Number(e.target.value))}
                      disabled={busy}
                    />
                  </label>
                  <button
                    type="button"
                    className="home-btn home-btn-secondary learn-btn--compact"
                    disabled={busy}
                    onClick={() => { setPageFrom(1); setPageTo(pageCount); }}
                  >
                    All {pageCount} pages
                  </button>
                </div>
                {!rangeOk && (
                  <p className="learn-pdf-range-error" role="alert">Enter a valid range between 1 and {pageCount}.</p>
                )}
              </fieldset>

              <fieldset className="learn-pdf-fieldset">
                <legend>Generation</legend>
                <div className="learn-pdf-select-row">
                  <label>
                    Questions
                    <select
                      className="learn-input"
                      value={questionMode}
                      onChange={(e) => setQuestionMode(e.target.value)}
                      disabled={busy}
                    >
                      {QUESTION_PRESETS.map((p) => (
                        <option key={p.id} value={p.id}>{p.label}</option>
                      ))}
                    </select>
                  </label>
                  {questionMode === 'CUSTOM' && (
                    <label>
                      Max
                      <input
                        type="number"
                        className="learn-input learn-pdf-num"
                        min={1}
                        max={analyze.planMaxQuestionsPerImport}
                        value={customQuestions}
                        onChange={(e) => setCustomQuestions(e.target.value)}
                        placeholder={`1–${analyze.planMaxQuestionsPerImport}`}
                        disabled={busy}
                      />
                    </label>
                  )}
                  <label>
                    Difficulty
                    <select className="learn-input" value={difficulty} onChange={(e) => setDifficulty(e.target.value)} disabled={busy}>
                      {DIFFICULTY.map((d) => <option key={d.id} value={d.id}>{d.label}</option>)}
                    </select>
                  </label>
                  <label>
                    Focus
                    <select className="learn-input" value={focus} onChange={(e) => setFocus(e.target.value)} disabled={busy}>
                      {FOCUS.map((f) => <option key={f.id} value={f.id}>{f.label}</option>)}
                    </select>
                  </label>
                </div>
                {liveEstimates && (
                  <p className="learn-muted learn-pdf-estimate">
                    ~{liveEstimates.chunks} text segment{liveEstimates.chunks === 1 ? '' : 's'}
                    {' · '}
                    up to ~{liveEstimates.questions} questions
                    {' · '}
                    {liveEstimates.span} page{liveEstimates.span === 1 ? '' : 's'} selected
                    {analyze.longRunning && (
                      <>
                        {' · '}
                        est. {analyze.estimatedMinutesMin}–{analyze.estimatedMinutesMax} min
                      </>
                    )}
                  </p>
                )}
              </fieldset>

              {analyze.longRunning && analyze.queueMessage && (
                <p className="learn-pdf-queue-note" role="note">{analyze.queueMessage}</p>
              )}

              <label className="learn-pdf-agree">
                <input
                  type="checkbox"
                  checked={agreed}
                  onChange={(e) => setAgreed(e.target.checked)}
                  disabled={busy}
                />
                I reviewed the analysis and options above and want to start processing.
              </label>

              <div className="learn-pdf-actions">
                <button
                  type="button"
                  className="home-btn home-btn-primary learn-btn--compact"
                  disabled={busy || !agreed || !rangeOk}
                  onClick={startImport}
                >
                  {busy ? 'Starting…' : 'Start PDF import'}
                </button>
                <button
                  type="button"
                  className="home-btn home-btn-secondary learn-btn--compact"
                  disabled={busy}
                  onClick={() => { setPdfFile(null); setAnalyze(null); setAgreed(false); }}
                >
                  Choose another file
                </button>
              </div>
            </>
          )}
        </div>
      )}

      <div className="learn-pdf-how">
        <button
          type="button"
          className="learn-pdf-how-toggle"
          aria-expanded={howOpen}
          onClick={() => setHowOpen((v) => !v)}
        >
          How PDF ingestion works
          {howOpen ? <LuChevronUp aria-hidden /> : <LuChevronDown aria-hidden />}
        </button>
        {howOpen && (
          <ol className="learn-pdf-how-steps">
            <li><strong>Analyze</strong> — We scan page count, size, embedded text, and whether OCR is needed for scans or handwriting.</li>
            <li><strong>You choose</strong> — Page range, question cap, difficulty, and focus before anything is generated.</li>
            <li><strong>Queue</strong> — Long PDFs join a background queue; you can leave the page and watch progress below.</li>
            <li><strong>Extract &amp; generate</strong> — Text is chunked; AI builds Q&amp;A grounded in your selected pages.</li>
            <li><strong>Review</strong> — You preview generated cards and confirm before they are saved to the notebook.</li>
          </ol>
        )}
      </div>
    </>
  );
}

export default forwardRef(LearnPdfImportPanel);
